import { useState } from 'react';
import { Alert, RadioCardGroup } from '@playerbase/ui';
import type { ThemeSensitivity } from '@playerbase/types';

import { useShell } from '../../shell/use-shell.js';
import { updateGame } from '../../games/game-data.js';

const SENSITIVITY_OPTIONS: ReadonlyArray<{
  value: ThemeSensitivity;
  label: string;
  description: string;
}> = [
  {
    value: 'high',
    label: 'High',
    description: 'Surface every callout, even when only one person mentions it.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Wait for a few mentions before lifting a callout into a theme.',
  },
  {
    value: 'low',
    label: 'Low',
    description: 'Only graduate well-established patterns that come up repeatedly.',
  },
];

// Sub-page under /g/:gameShortId/feedback-settings/insights. Controls the
// per-game theme_sensitivity dial. The dial only affects future snapshots;
// themes that have already been formed at a different sensitivity are
// untouched. The Insights page itself stays clean - this is the single
// knob a studio gets over how readily callouts graduate into themes.
export function InsightsSection(): JSX.Element {
  const { activeGame, refresh } = useShell();
  const [pending, setPending] = useState<ThemeSensitivity | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (activeGame === null) {
    // Route lives under /g/:gameShortId - shouldn't be reachable without a
    // game, but guard against a render race during navigation.
    return <div className="text-sm text-text-muted">No game selected.</div>;
  }

  const current = pending ?? activeGame.themeSensitivity;

  async function handleChange(next: string): Promise<void> {
    if (activeGame === null) return;
    const value = next as ThemeSensitivity;
    if (value === current) return;
    setPending(value);
    setError(null);
    const result = await updateGame(activeGame.id, { themeSensitivity: value });
    if (result === null) {
      setPending(null);
      setError('Could not save your sensitivity choice. Please try again.');
      return;
    }
    // Pull the shell's view of the game back in line with what we just
    // wrote, so other surfaces (and a fresh mount of this page) see the
    // new value without waiting for a hard reload.
    await refresh();
    setPending(null);
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-semibold text-text">Pattern detection</h1>
        <p className="text-sm text-text-muted">
          Tune how quickly the patterns we surface from your Discord activity graduate
          into named themes. Pick what fits your community&rsquo;s volume.
        </p>
      </header>

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}

      <div className="flex flex-col gap-3">
        <span className="text-sm font-medium text-text">Sensitivity</span>
        <RadioCardGroup
          name="theme-sensitivity"
          value={current}
          onChange={(v) => void handleChange(v)}
          options={SENSITIVITY_OPTIONS}
          orientation="grid"
          columns={3}
          aria-label="Theme sensitivity"
          disabled={pending !== null}
        />
        <p className="text-xs text-text-muted">
          New themes use the setting at the time of their snapshot. Themes already
          surfaced won&rsquo;t be removed if you change this.
        </p>
      </div>
      </div>
    </div>
  );
}
